// v3.15-934: Admin-only "Reassign Concierge" modal. Lets a PTC admin pick a
// new application_concierge for any claimed application without leaving the
// dashboard. Backed by `POST /v2/applications/{id}/assign` with payload
// `{override: true, new_user_id}` — server-side guards reject non-admin
// callers and log an `application_concierge_reassigned` activity.
import { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Loader2, AlertTriangle, UserCheck } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

export default function ReassignConciergeModal({
  open,
  onOpenChange,
  application,
  onReassigned,
}) {
  const [users, setUsers]       = useState([]);
  const [loading, setLoading]   = useState(false);
  const [search, setSearch]     = useState('');
  const [selected, setSelected] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const currentConciergeId =
    application?.application_concierge_id ||
    application?.claimed_by ||
    application?.ptc_rep_id ||
    application?.sales_rep_id ||
    null;
  const currentConciergeName =
    application?.application_concierge_name ||
    application?.ptc_rep_name ||
    application?.sales_rep_name ||
    'unassigned';

  // Reset on open and load eligible users (PTC company).
  useEffect(() => {
    if (!open) return;
    setSelected(null);
    setSearch('');
    setLoading(true);
    api.get('/users')
      .then(({ data }) => {
        // Only PTC users can be concierges (matches the backend `is_ptc_user` check).
        const filtered = (data || []).filter((u) => {
          const role = (u.role || '').toLowerCase();
          return u.is_active !== false && (role.startsWith('ptc_') || role === 'ptc_admin' || role.includes('concierge') || role.includes('rep'));
        });
        // Sort by name; current concierge sinks to the bottom.
        filtered.sort((a, b) => {
          if (a.id === currentConciergeId) return 1;
          if (b.id === currentConciergeId) return -1;
          return (a.full_name || a.email || '').localeCompare(b.full_name || b.email || '');
        });
        setUsers(filtered);
      })
      .catch(() => toast.error('Failed to load users'))
      .finally(() => setLoading(false));
  }, [open, currentConciergeId]);

  const visibleUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) =>
      (u.full_name || '').toLowerCase().includes(q) ||
      (u.email || '').toLowerCase().includes(q)
    );
  }, [users, search]);

  const submit = async () => {
    if (!selected) {
      toast.error('Pick a new concierge');
      return;
    }
    if (selected.id === currentConciergeId) {
      toast.warning('Selected user is already the concierge');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/v2/applications/${application.id}/assign`, {
        override:    true,
        new_user_id: selected.id,
      });
      toast.success(`Concierge reassigned to ${data?.application_concierge_name || selected.full_name}`);
      onReassigned && onReassigned({
        applicationId: application.id,
        clientId:      application.client_id,
        application_concierge_id:   data?.application_concierge_id,
        application_concierge_name: data?.application_concierge_name,
      });
      onOpenChange(false);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || 'Reassignment failed';
      if (status === 403) {
        toast.error('Admin access required to reassign concierge');
      } else {
        toast.error(detail);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="reassign-concierge-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserCheck className="h-4 w-4 text-cyan-600" />
            Reassign Application Concierge
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          {/* Spec-required confirmation copy */}
          <div className="text-sm text-slate-700 bg-amber-50 border border-amber-200 rounded px-3 py-2.5 flex items-start gap-2" data-testid="reassign-confirm-copy">
            <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
            <div>
              Reassign concierge from{' '}
              <span className="font-semibold text-slate-900">{currentConciergeName}</span>{' '}
              to another user?
            </div>
          </div>

          {/* Application context */}
          <div className="text-xs text-slate-500 space-y-0.5">
            <div>
              Application:{' '}
              <span className="font-mono text-slate-700">{application?.application_number || application?.id?.slice(0, 8)}</span>
            </div>
            {application?.full_name && (
              <div>
                Client: <span className="font-medium text-slate-700">{application.full_name}</span>
              </div>
            )}
          </div>

          {/* User picker */}
          <div>
            <label className="text-xs font-semibold text-slate-700 block mb-1">Select new concierge</label>
            <Input
              placeholder="Search by name or email…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="reassign-user-search"
            />
            <div
              className="mt-2 max-h-60 overflow-y-auto border border-slate-200 rounded divide-y divide-slate-100"
              data-testid="reassign-user-list"
            >
              {loading ? (
                <div className="flex items-center justify-center py-4 text-xs text-slate-400 gap-2">
                  <Loader2 className="h-3 w-3 animate-spin" /> Loading users…
                </div>
              ) : visibleUsers.length === 0 ? (
                <div className="text-center py-4 text-xs text-slate-400">No matching users</div>
              ) : (
                visibleUsers.map((u) => {
                  const isCurrent = u.id === currentConciergeId;
                  const isSelected = selected?.id === u.id;
                  return (
                    <button
                      key={u.id}
                      type="button"
                      onClick={() => !isCurrent && setSelected(u)}
                      disabled={isCurrent}
                      className={`w-full text-left px-3 py-2 text-xs transition-colors ${
                        isCurrent
                          ? 'opacity-50 cursor-not-allowed bg-slate-50'
                          : isSelected
                            ? 'bg-cyan-50 border-l-2 border-cyan-500'
                            : 'hover:bg-slate-50'
                      }`}
                      data-testid={`reassign-user-option-${u.id}`}
                    >
                      <div className="font-medium text-slate-800">
                        {u.full_name || u.email}
                        {isCurrent && (
                          <span className="ml-1.5 text-[10px] text-slate-400 font-normal">(current concierge)</span>
                        )}
                      </div>
                      <div className="text-[11px] text-slate-500">
                        {u.email}
                        {u.role && <span className="ml-1.5 px-1 py-0.5 bg-slate-100 text-slate-600 rounded text-[10px] uppercase tracking-wide">{u.role.replace(/_/g, ' ')}</span>}
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
            data-testid="reassign-cancel"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={submit}
            disabled={submitting || !selected || selected.id === currentConciergeId}
            className="bg-cyan-600 hover:bg-cyan-500"
            data-testid="reassign-confirm"
          >
            {submitting ? <><Loader2 className="h-3 w-3 animate-spin mr-1" /> Reassigning…</> : 'Confirm Reassign'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
